import React from "react";
import { motion } from "framer-motion";
import { ShieldCheck, UserCheck, AlertTriangle, Eye, ShieldAlert, HeartHandshake } from "lucide-react";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";

export default function SafetyPage() {
  const safetyTips = [
    {
      title: "Meet in Public",
      description: "Always meet the seller or buyer in a busy, public place like a shopping mall or café.",
      icon: UserCheck
    },
    {
      title: "Inspect the Item",
      description: "Never pay for an item before you've seen and inspected it in person to ensure it matches the description.",
      icon: Eye
    },
    {
      title: "Avoid Prepayments",
      description: "HuluOrder does not handle payments. Never send money in advance via bank transfer or mobile money.",
      icon: AlertTriangle
    },
    {
      title: "Trust Your Gut",
      description: "If a deal seems too good to be true, it probably is. Don't feel pressured to complete a transaction.",
      icon: ShieldAlert
    }
  ];

  return (
    <div className="min-h-screen bg-white font-sans text-gray-900">
      <Header />

      <main>
        {/* Hero Section */}
        <section className="relative py-24 overflow-hidden bg-gray-50">
          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-gray-900 via-gray-900 to-green-900/30 -z-10"></div>
          <div className="max-w-7xl mx-auto px-6 text-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-8"
            >
              <ShieldCheck className="w-10 h-10 text-green-400" />
            </motion.div>
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-4xl md:text-6xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-green-400 to-emerald-400"
            >
              Trust & Safety
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-lg text-gray-400 max-w-2xl mx-auto"
            >
              Your safety is our priority. We work hard to keep HuluOrder 
              a safe and reliable place to trade.
            </motion.p>
          </div>
        </section>

        {/* Safety Tips Grid */}
        <section className="py-24 max-w-7xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-center mb-16">Core Safety Rules</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {safetyTips.map((tip, i) => (
              <motion.div 
                key={i}
                whileHover={{ x: 10 }}
                className="p-8 rounded-[2rem] bg-white border border-gray-100 shadow-sm flex items-start gap-6 group hover:border-green-500/30 transition-all"
              >
                <div className="flex-shrink-0 w-16 h-16 bg-green-50 rounded-2xl flex items-center justify-center group-hover:bg-green-500 group-hover:text-white transition-colors">
                  <tip.icon className="w-8 h-8 text-green-600 group-hover:text-white transition-colors" />
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-3">{tip.title}</h3>
                  <p className="text-gray-600 leading-relaxed text-sm">
                    {tip.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Verified Seller Section */}
        <section className="py-24 bg-gray-900 text-white overflow-hidden relative">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-[radial-gradient(circle_at_center,rgba(34,197,94,0.1),transparent_70%)]"></div>
          <div className="max-w-7xl mx-auto px-6 relative flex flex-col lg:flex-row items-center gap-16">
            <div className="flex-1 space-y-8">
              <h2 className="text-4xl font-bold leading-tight">Look for the <span className="text-green-400">Verified</span> Badge</h2>
              <p className="text-gray-400 text-lg leading-relaxed">
                To increase safety, we encourage sellers to undergo our verification process. 
                Sellers with a green checkmark have verified their phone numbers and provided 
                valid identification.
              </p>
              <ul className="space-y-4">
                <li className="flex items-center gap-3">
                  <HeartHandshake className="w-5 h-5 text-green-400" />
                  <span>Enhanced security for buyers</span>
                </li>
                <li className="flex items-center gap-3">
                  <ShieldCheck className="w-5 h-5 text-green-400" />
                  <span>Building community trust</span>
                </li>
              </ul>
            </div>
            <div className="flex-1 flex justify-center">
              <div className="w-64 h-64 bg-green-500/20 rounded-full flex items-center justify-center animate-pulse">
                 <ShieldCheck className="w-32 h-32 text-green-500 drop-shadow-[0_0_20px_rgba(34,197,94,0.5)]" />
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
